import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { FarmInfo, MAX_FARMS_PER_DRAFT } from '../../../../types/clientOnboarding';

interface Props {
  farms: FarmInfo[];
  onChange: (farms: FarmInfo[]) => void;
  errors: Record<string, string>;
}

const inputCls =
  'w-full p-2.5 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent';
const labelCls = 'block text-sm font-medium text-[var(--text-secondary)] mb-1';
const errorCls = 'text-[var(--color-danger)] text-xs mt-1';

const FarmStep: React.FC<Props> = ({ farms, onChange, errors }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  const update = (idx: number, key: keyof FarmInfo, value: string | number | undefined) => {
    const next = farms.map((f, i) => (i === idx ? { ...f, [key]: value } : f));
    onChange(next);
  };

  const addFarm = () => {
    onChange([...farms, { farm_name: '', address: '', country: '' }]);
  };

  // ---- R1A-03: Remove farm (minimum 1) ----
  const removeFarm = (idx: number) => {
    if (farms.length <= 1) return;
    onChange(farms.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4" data-testid="onboarding-step-farm">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step2.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step2.description')}</p>
      </div>

      {farms.map((farm, idx) => (
        <div key={idx} className="border border-[var(--border-light)] rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-[var(--text-primary)]">{t('step2.farm_name')} {idx + 1}</h4>
            {farms.length > 1 && (
              <button
                onClick={() => removeFarm(idx)}
                className="p-1.5 text-[var(--text-tertiary)] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                aria-label={t('step2.removeFarm', { index: idx + 1 })}
                data-testid={`onboarding-remove-farm-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor={`farm-${idx}-name`}>{t('step2.farm_name')} *</label>
              <input id={`farm-${idx}-name`} className={inputCls} value={farm.farm_name}
                onChange={e => update(idx, 'farm_name', e.target.value)}
                placeholder={t('step2.farm_name_ph')} data-testid={`onboarding-farm-name-${idx}`}
                aria-describedby={errors[`farms.${idx}.farm_name`] ? `farm-${idx}-name-err` : undefined} />
              {errors[`farms.${idx}.farm_name`] && <p id={`farm-${idx}-name-err`} className={errorCls} role="alert">{errors[`farms.${idx}.farm_name`]}</p>}
            </div>
            <div>
              <label className={labelCls} htmlFor={`farm-${idx}-country`}>{t('step2.country')}</label>
              <input id={`farm-${idx}-country`} className={inputCls} value={farm.country}
                onChange={e => update(idx, 'country', e.target.value)} data-testid={`onboarding-farm-country-${idx}`}
                aria-invalid={errors[`farms.${idx}.country`] ? true : undefined}
                aria-describedby={errors[`farms.${idx}.country`] ? `farm-${idx}-country-err` : undefined} />
              {errors[`farms.${idx}.country`] && <p id={`farm-${idx}-country-err`} className={errorCls} role="alert">{errors[`farms.${idx}.country`]}</p>}
            </div>
            <div className="md:col-span-2">
              <label className={labelCls} htmlFor={`farm-${idx}-address`}>{t('step2.address')}</label>
              <input id={`farm-${idx}-address`} className={inputCls} value={farm.address}
                onChange={e => update(idx, 'address', e.target.value)}
                placeholder={t('step2.address_ph')} data-testid={`onboarding-farm-address-${idx}`} />
            </div>
            <div>
              <label className={labelCls} htmlFor={`farm-${idx}-surface`}>{t('step2.surface_ha')}</label>
              <input id={`farm-${idx}-surface`} className={inputCls} type="number" step="0.01" value={farm.surface_ha ?? ''}
                onChange={e => update(idx, 'surface_ha', e.target.value ? Number(e.target.value) : undefined)}
                placeholder={t('step2.surface_ph')} data-testid={`onboarding-farm-surface-${idx}`}
                aria-describedby={errors[`farms.${idx}.surface_ha`] ? `farm-${idx}-surface-err` : undefined} />
              {errors[`farms.${idx}.surface_ha`] && <p id={`farm-${idx}-surface-err`} className={errorCls} role="alert">{errors[`farms.${idx}.surface_ha`]}</p>}
            </div>
            <div>
              <label className={labelCls} htmlFor={`farm-${idx}-lat`}>{t('step2.latitude')}</label>
              <input id={`farm-${idx}-lat`} className={inputCls} type="number" step="0.000001" value={farm.latitude ?? ''}
                onChange={e => update(idx, 'latitude', e.target.value ? Number(e.target.value) : undefined)}
                placeholder={t('step2.latitude_ph')} data-testid={`onboarding-farm-lat-${idx}`}
                aria-invalid={errors[`farms.${idx}.latitude`] ? true : undefined}
                aria-describedby={errors[`farms.${idx}.latitude`] ? `farm-${idx}-lat-err` : undefined} />
              {errors[`farms.${idx}.latitude`] && <p id={`farm-${idx}-lat-err`} className={errorCls} role="alert">{errors[`farms.${idx}.latitude`]}</p>}
            </div>
            <div>
              <label className={labelCls} htmlFor={`farm-${idx}-lon`}>{t('step2.longitude')}</label>
              <input id={`farm-${idx}-lon`} className={inputCls} type="number" step="0.000001" value={farm.longitude ?? ''}
                onChange={e => update(idx, 'longitude', e.target.value ? Number(e.target.value) : undefined)}
                placeholder={t('step2.longitude_ph')} data-testid={`onboarding-farm-lon-${idx}`}
                aria-invalid={errors[`farms.${idx}.longitude`] ? true : undefined}
                aria-describedby={errors[`farms.${idx}.longitude`] ? `farm-${idx}-lon-err` : undefined} />
              {errors[`farms.${idx}.longitude`] && <p id={`farm-${idx}-lon-err`} className={errorCls} role="alert">{errors[`farms.${idx}.longitude`]}</p>}
            </div>
          </div>
        </div>
      ))}

      {farms.length < MAX_FARMS_PER_DRAFT ? (
        <button
          onClick={addFarm}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-[var(--border-light)] rounded-lg text-[var(--text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
          data-testid="onboarding-add-farm"
        >
          <Plus className="w-4 h-4" /> {t('step2.addFarm')}
        </button>
      ) : (
        <button
          disabled
          className="flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-[var(--border-light)] rounded-lg text-[var(--text-tertiary)] cursor-not-allowed opacity-50"
          data-testid="onboarding-add-farm-disabled"
          title={t('validation.tooManyItems', { max: MAX_FARMS_PER_DRAFT })}
        >
          <Plus className="w-4 h-4" /> {t('step2.addFarm')}
        </button>
      )}

      <p className="text-xs text-[var(--text-tertiary)]">{t('step2.multiFarmNote')}</p>

      {errors['farms.empty'] && <p className={errorCls} role="alert">{errors['farms.empty']}</p>}
      {errors['farms.too_many'] && <p className={errorCls} role="alert">{errors['farms.too_many']}</p>}
    </div>
  );
};

export default FarmStep;
